/**
 * Admin Remote Hosts Management Module
 *
 * Host CRUD for the Manage Hosts page.
 */

import { api } from '../api/client.js';
import type { RemoteHost, RemoteHostRequest } from '../api/types.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded, confirmDialog, notificationDialog } from '../ui/dialogs.js';

let hostsTable: unknown = null;

/**
 * Fill the hosts table with host information
 */
export async function fillHostsTable(): Promise<void> {
  const response = await apiCall(api.listHosts(), 'Failed to Load Hosts');
  if (!response) return;

  const tableData: Array<{
    name: string;
    control_url: string;
    public_key: string;
    status: string;
    latency: string;
    shared_bridges: string;
    actions: string;
  }> = [];

  // Add local host (read-only, but with copy public key action)
  const localPubKey = response.local.public_key || '';
  tableData.push({
    name: `${escapeHtml(response.local.name || 'localhost')} <span class="badge badge-success">local</span>`,
    control_url: escapeHtml(response.local.control_url || '-'),
    public_key: localPubKey
      ? `<span class="monospace" title="${escapeAttr(localPubKey)}">${escapeHtml(localPubKey.substring(0, 16))}...</span>`
      : '-',
    status: '<span class="badge badge-success">online</span>',
    latency: '-',
    shared_bridges: '-',
    actions: localPubKey
      ? `<button class="ui-button" onclick='copyPublicKey("${escapeAttr(localPubKey)}")'>Copy Public Key</button>`
      : '',
  });

  // Collect remote host names for health checks
  const remoteHostNames: string[] = [];

  // Add remote hosts
  for (const host of response.hosts) {
    const safeName = escapeAttr(host.name);
    remoteHostNames.push(host.name);

    tableData.push({
      name: escapeHtml(host.name),
      control_url: escapeHtml(host.control_url || '-'),
      public_key: host.public_key
        ? `<span class="monospace" title="${escapeAttr(host.public_key)}">${escapeHtml(host.public_key.substring(0, 16))}...</span>`
        : '-',
      status: `<span class="badge badge-unknown" data-host-status="${safeName}">checking...</span>`,
      latency: `<span data-host-latency="${safeName}">-</span>`,
      shared_bridges: `<span data-host-bridges="${safeName}">-</span>`,
      actions: `
        <button class="ui-button" onclick='testHost("${safeName}")'>Test</button>
        <button class="ui-button" onclick='modifyHost("${safeName}")'>Modify</button>
        <button class="ui-button" onclick='deleteHost("${safeName}")'>Delete</button>
      `,
    });
  }

  if (!hostsTable) {
    hostsTable = $('#hosts').DataTable({
      data: tableData,
      columns: [
        { data: 'name', title: 'Name' },
        { data: 'control_url', title: 'Control URL' },
        { data: 'public_key', title: 'Public Key' },
        { data: 'status', title: 'Status' },
        { data: 'latency', title: 'Latency' },
        { data: 'shared_bridges', title: 'Shared Networks' },
        { data: 'actions', title: 'Actions' },
      ],
      paging: false,
      info: false,
      searching: false,
    });
  } else {
    const dt = hostsTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }

  declareLoaded();

  // Fire off health checks for all remote hosts in parallel
  for (const hostName of remoteHostNames) {
    checkHostHealth(hostName);
  }
}

/**
 * Check health of a remote host and update the table cells
 */
async function checkHostHealth(name: string): Promise<void> {
  const statusCell = $(`[data-host-status="${escapeAttr(name)}"]`);
  const latencyCell = $(`[data-host-latency="${escapeAttr(name)}"]`);
  const bridgesCell = $(`[data-host-bridges="${escapeAttr(name)}"]`);

  try {
    const result = await api.testRemoteHost(name);
    if (result.success) {
      statusCell
        .removeClass('badge-unknown')
        .addClass('badge-success')
        .text('online');
      latencyCell.text(`${result.latency_ms || 0}ms`);

      // Show shared bridges
      if (result.shared_bridges && result.shared_bridges.length > 0) {
        bridgesCell.text(result.shared_bridges.join(', '));
      } else {
        bridgesCell.text('none');
      }
    } else {
      statusCell
        .removeClass('badge-unknown')
        .addClass('badge-error')
        .text('offline');
      latencyCell.text('-');
      bridgesCell.text('-');
    }
  } catch {
    statusCell
      .removeClass('badge-unknown')
      .addClass('badge-error')
      .text('error');
    latencyCell.text('-');
    bridgesCell.text('-');
  }
}

/**
 * Build the host form HTML.
 */
function buildHostForm(host?: RemoteHost): string {
  const name = host?.name || '';
  const controlUrl = host?.control_url || '';
  const publicKey = host?.public_key || '';

  return `
    <div class="form-field">
      <label>Name</label>
      <input id="host-name" value="${escapeAttr(name)}" ${host ? 'readonly' : ''} placeholder="e.g. worker1">
    </div>
    <div class="form-field">
      <label>Control URL</label>
      <input id="host-control-url" value="${escapeAttr(controlUrl)}" placeholder="e.g. http://192.168.1.10:8080">
    </div>
    <div class="form-field">
      <label>Public Key</label>
      <textarea id="host-public-key" rows="2" placeholder="Ed25519 public key (hex)">${escapeHtml(publicKey)}</textarea>
    </div>
  `;
}

/**
 * Collect host form values into an object.
 */
function collectHostForm(): RemoteHostRequest & { name: string } {
  const name = ($('#host-name').val() as string || '').trim();
  const controlUrl = ($('#host-control-url').val() as string || '').trim();
  const publicKey = ($('#host-public-key').val() as string || '').trim();

  return {
    name,
    control_url: controlUrl || undefined,
    public_key: publicKey || undefined,
  };
}

/**
 * Create a new remote host
 */
export async function createHost(): Promise<void> {
  const form = buildHostForm();

  let saving = false;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Add Remote Host</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Add Remote Host',
      zIndex: 10000,
      autoOpen: true,
      width: 500,
      resizable: false,
      buttons: {
        Create: async function () {
          if (saving) return;
          saving = true;

          const data = collectHostForm();
          if (!data.name) {
            alert('Please enter a host name');
            saving = false;
            return;
          }
          if (!data.control_url) {
            alert('Please enter a control URL');
            saving = false;
            return;
          }

          const parent = $(this);
          declareLoading();

          const result = await apiCall(api.createRemoteHost(data), 'Failed to Create Host');
          if (!result) {
            declareLoaded();
            saving = false;
            return;
          }

          declareLoaded();
          parent.dialog('close');
          fillHostsTable();
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Modify an existing remote host
 */
export async function modifyHost(name: string): Promise<void> {
  declareLoading();

  const host = await apiCall(api.getRemoteHost(name), 'Failed to Load Host');
  if (!host) {
    declareLoaded();
    return;
  }

  const form = buildHostForm(host);
  declareLoaded();

  let saving = false;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Modify Host</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Modify Host: ' + escapeHtml(name),
      zIndex: 10000,
      autoOpen: true,
      width: 500,
      resizable: false,
      buttons: {
        Save: async function () {
          if (saving) return;
          saving = true;

          const data = collectHostForm();
          const parent = $(this);
          declareLoading();

          const result = await apiCall(api.updateRemoteHost(name, data), 'Failed to Update Host');
          if (!result) {
            declareLoaded();
            saving = false;
            return;
          }

          declareLoaded();
          parent.dialog('close');
          fillHostsTable();
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Delete a remote host
 */
export async function deleteHost(name: string): Promise<void> {
  confirmDialog(
    `Are you sure you want to delete remote host '${escapeHtml(name)}'?`,
    async () => {
      declareLoading();
      await apiCall(api.deleteRemoteHost(name), 'Failed to Delete Host');
      await fillHostsTable();
      declareLoaded();
    }
  );
}

/**
 * Copy public key to clipboard
 */
export function copyPublicKey(pubKey: string): void {
  navigator.clipboard.writeText(pubKey).then(() => {
    notificationDialog('Public key copied to clipboard. Share this with remote host administrators to establish trust.');
  }).catch(() => {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = pubKey;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    notificationDialog('Public key copied to clipboard. Share this with remote host administrators to establish trust.');
  });
}

/**
 * Test connectivity to a remote host
 */
export async function testHost(name: string): Promise<void> {
  // Update status to show we're checking
  const statusCell = $(`[data-host-status="${escapeAttr(name)}"]`);
  const latencyCell = $(`[data-host-latency="${escapeAttr(name)}"]`);
  const bridgesCell = $(`[data-host-bridges="${escapeAttr(name)}"]`);
  statusCell.removeClass('badge-success badge-error').addClass('badge-unknown').text('checking...');

  declareLoading();

  const result = await apiCall(api.testRemoteHost(name), 'Failed to Test Host');
  declareLoaded();

  if (result) {
    if (result.success) {
      // Update table status
      statusCell.removeClass('badge-unknown').addClass('badge-success').text('online');
      latencyCell.text(`${result.latency_ms || 0}ms`);

      // Update shared bridges
      const bridges = result.shared_bridges || [];
      bridgesCell.text(bridges.length > 0 ? bridges.join(', ') : 'none');

      notificationDialog(
        `<p><strong>Connection successful!</strong></p>` +
        `<p>Peer name: ${escapeHtml(result.peer_name || 'unknown')}</p>` +
        `<p>Latency: ${result.latency_ms || 0}ms</p>` +
        `<p>Shared networks: ${bridges.length > 0 ? escapeHtml(bridges.join(', ')) : 'none'}</p>`,
        true
      );
    } else {
      // Update table status
      statusCell.removeClass('badge-unknown').addClass('badge-error').text('offline');
      latencyCell.text('-');
      bridgesCell.text('-');

      notificationDialog(
        `<p><strong>Connection failed</strong></p>` +
        `<p>Error: ${escapeHtml(result.error || 'Unknown error')}</p>`,
        true
      );
    }
  }
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).fillHostsTable = fillHostsTable;
  (window as unknown as Record<string, unknown>).createHost = createHost;
  (window as unknown as Record<string, unknown>).modifyHost = modifyHost;
  (window as unknown as Record<string, unknown>).deleteHost = deleteHost;
  (window as unknown as Record<string, unknown>).testHost = testHost;
  (window as unknown as Record<string, unknown>).copyPublicKey = copyPublicKey;
}
